import { buildWordTool, type WordTool } from './builder';

const MARGIN_KEYS = ['top_margin', 'bottom_margin', 'left_margin', 'right_margin'] as const;
const PAGE_SETUP_KEYS = [
  'orientation',
  'top_margin',
  'bottom_margin',
  'left_margin',
  'right_margin',
  'paper_size',
] as const;

export const structureTools: WordTool[] = [
  buildWordTool({
    name: 'insert_break',
    description:
      "Insert a page break or section break into the document. Use 'page' for a simple page break (new page, same section). Use 'section_next_page' for a new section on a new page (needed for different headers/footers). Use 'section_continuous' for a new section without a page break. For controlling page breaks at the paragraph level, use format_paragraph with page_break_before instead. Section breaks split the document — existing header/footer settings may not carry forward. Returns break type and location; for section breaks, hints to check updated section count.",
    parameters: {
      type: 'object',
      properties: {
        type: {
          type: 'string',
          enum: ['page', 'section_next_page', 'section_continuous'],
          description:
            "Break type. 'page': new page, same section. 'section_next_page': new section starting on next page — use when you need different headers/footers per section. 'section_continuous': new section on same page — use for column layout changes mid-page.",
        },
        location: {
          type: 'string',
          enum: ['end', 'after_selection'],
          description:
            "Where to insert. 'end': after all body content (safe default). 'after_selection': after the cursor — call get_selection first to verify cursor position.",
        },
      },
      required: ['type', 'location'],
    },
    permission: true,
    prerequisites: (args) =>
      args.location === 'after_selection' ? { needsSelection: true } : null,
    validateInput: (args) => {
      const allowed = ['page', 'section_next_page', 'section_continuous'];
      if (!allowed.includes(args.type as string)) {
        return {
          error: `Unknown break type "${args.type}".`,
          recovery: "Use 'page', 'section_next_page', or 'section_continuous'.",
        };
      }
      return null;
    },
    getActivityDescription: (args) => `Inserting ${args.type} break at ${args.location}`,
    execute: async (args) => {
      const breakMap: Record<string, Word.BreakType> = {
        page: Word.BreakType.page,
        section_next_page: Word.BreakType.sectionNext,
        section_continuous: Word.BreakType.sectionContinuous,
      };
      const breakType = breakMap[args.type as string]!;

      return await Word.run(async (context) => {
        if (args.location === 'after_selection') {
          context.document.getSelection().insertBreak(breakType, Word.InsertLocation.after);
        } else {
          context.document.body.insertBreak(breakType, Word.InsertLocation.end);
        }
        await context.sync();

        const isSectionBreak = (args.type as string).startsWith('section');
        return JSON.stringify({
          success: true,
          break_type: args.type,
          location: args.location,
          hint: isSectionBreak
            ? 'New section created. Use get_document to check the updated section count, then set_header_footer for the new section if needed.'
            : undefined,
        });
      });
    },
  }),

  buildWordTool({
    name: 'set_header_footer',
    description:
      "Set the content of a header or footer for one or more sections in a single call. REPLACES existing content. For page numbers, use insert_page_numbers (which can include surrounding text via prefix_text/suffix_text). To apply identical content to multiple sections at once, pass section_indices instead of section_index.",
    parameters: {
      type: 'object',
      properties: {
        type: {
          type: 'string',
          enum: ['header', 'footer'],
          description: 'Header (top) or footer (bottom) of the page.',
        },
        text: {
          type: 'string',
          description: 'Replaces all existing content. Cannot be empty — use a single space " " to clear.',
        },
        section_index: {
          type: 'number',
          description: 'Single section (0-based). Defaults to 0. Mutually exclusive with section_indices.',
        },
        section_indices: {
          type: 'array',
          description:
            'Apply to multiple sections in one call. Example: [0, 1, 2]. Mutually exclusive with section_index. Use this to avoid N separate calls for an N-section document.',
        },
        header_footer_type: {
          type: 'string',
          enum: ['primary', 'firstPage', 'evenPages'],
          description:
            "'primary' (default), 'firstPage' (requires 'different first page'), 'evenPages' (requires 'different odd/even').",
        },
      },
      required: ['type', 'text'],
    },
    permission: true,
    isDestructive: () => true,
    validateInput: (args) => {
      if (typeof args.text !== 'string' || (args.text as string).length === 0) {
        return {
          error: 'text is required and cannot be empty.',
          recovery: 'Provide the header/footer text. To clear it, use a single space.',
        };
      }
      if (typeof args.section_index === 'number' && Array.isArray(args.section_indices)) {
        return {
          error: 'Provide either section_index or section_indices, not both.',
          recovery: 'Use section_indices for multiple sections, section_index for one.',
        };
      }
      if (typeof args.section_index === 'number' && (args.section_index as number) < 0) {
        return {
          error: 'section_index must be >= 0.',
          recovery: 'Use get_document to check the section count. Section 0 is the first section.',
        };
      }
      if (Array.isArray(args.section_indices)) {
        if (args.section_indices.length === 0) {
          return {
            error: 'section_indices is empty.',
            recovery: 'Provide at least one section index.',
          };
        }
        for (const s of args.section_indices as number[]) {
          if (typeof s !== 'number' || s < 0) {
            return {
              error: `section_indices contains invalid value ${s}.`,
              recovery: 'All values must be non-negative integers.',
            };
          }
        }
      }
      return null;
    },
    getActivityDescription: (args) => {
      if (Array.isArray(args.section_indices))
        return `Setting ${args.type} for ${args.section_indices.length} sections`;
      return `Setting ${args.type} for section ${
        typeof args.section_index === 'number' ? args.section_index : 0
      }`;
    },
    execute: async (args) => {
      return await Word.run(async (context) => {
        const sections = context.document.sections;
        sections.load('items');
        await context.sync();

        const indices: number[] = Array.isArray(args.section_indices)
          ? (args.section_indices as number[])
          : [typeof args.section_index === 'number' ? (args.section_index as number) : 0];

        const headerFooterType = (args.header_footer_type as string) ?? 'primary';
        const typeMap: Record<string, string> = {
          primary: Word.HeaderFooterType.primary,
          firstPage: Word.HeaderFooterType.firstPage,
          evenPages: Word.HeaderFooterType.evenPages,
        };
        const hfType = typeMap[headerFooterType] ?? Word.HeaderFooterType.primary;

        const perSection: Array<Record<string, unknown>> = [];
        for (const idx of indices) {
          const section = sections.items[idx];
          if (!section) {
            perSection.push({
              section_index: idx,
              error: `Section ${idx} out of range (document has ${sections.items.length} sections).`,
            });
            continue;
          }
          const target =
            args.type === 'header'
              ? section.getHeader(hfType as Word.HeaderFooterType)
              : section.getFooter(hfType as Word.HeaderFooterType);
          target.clear();
          target.insertParagraph(args.text as string, Word.InsertLocation.start);
          perSection.push({ section_index: idx, success: true });
        }
        await context.sync();

        const textPreview =
          (args.text as string).length > 60
            ? (args.text as string).slice(0, 60) + '...'
            : (args.text as string);

        if (Array.isArray(args.section_indices)) {
          return JSON.stringify({
            success: perSection.every((r) => r.success),
            type: args.type,
            text_preview: textPreview,
            sections: perSection,
          });
        }
        const only = perSection[0]!;
        if (only.error) {
          return JSON.stringify({
            error: only.error,
            recovery: `Use an index from 0 to ${sections.items.length - 1}.`,
          });
        }
        return JSON.stringify({
          success: true,
          type: args.type,
          section_index: only.section_index,
          text_preview: textPreview,
          hint: 'Headers/footers are not visible in get_document. Ask the user to verify.',
        });
      });
    },
  }),

  buildWordTool({
    name: 'insert_page_numbers',
    description:
      "Insert automatic page numbers into a header or footer, optionally with surrounding text. Each call CLEARS the target header/footer first. To combine page numbers with brand or copyright text IN THE SAME LOCATION, use prefix_text/suffix_text here rather than a separate set_header_footer call. To apply to multiple sections, use section_indices.",
    parameters: {
      type: 'object',
      properties: {
        location: {
          type: 'string',
          enum: ['header', 'footer'],
          description: "Where to place page numbers. Defaults to 'footer'.",
        },
        alignment: {
          type: 'string',
          enum: ['left', 'center', 'right'],
          description: "Horizontal alignment. Defaults to 'center'.",
        },
        format: {
          type: 'string',
          enum: [
            '{PAGE}',
            'Page {PAGE}',
            'Page {PAGE} of {NUMPAGES}',
            '- {PAGE} -',
            '{PAGE} / {NUMPAGES}',
          ],
          description: "Page number format. {PAGE} = current page, {NUMPAGES} = total. Defaults to '{PAGE}'.",
        },
        prefix_text: {
          type: 'string',
          description:
            'Text inserted BEFORE the page-number field. Use to fuse brand text with numbers in one call instead of a separate set_header_footer. Example: "Acme Corp   ".',
        },
        suffix_text: {
          type: 'string',
          description:
            'Text inserted AFTER the page-number field. Example: "   © 2026". Use with prefix_text for mixed content in one call.',
        },
        section_index: {
          type: 'number',
          description:
            'Single section (0-based). Defaults to 0. Mutually exclusive with section_indices.',
        },
        section_indices: {
          type: 'array',
          description:
            'Apply to multiple sections in one call. Example: [0, 1]. Mutually exclusive with section_index.',
        },
      },
    },
    permission: true,
    isDestructive: () => true,
    validateInput: (args) => {
      if (typeof args.section_index === 'number' && Array.isArray(args.section_indices)) {
        return {
          error: 'Provide either section_index or section_indices, not both.',
          recovery: 'Use section_indices for multiple sections.',
        };
      }
      if (typeof args.section_index === 'number' && (args.section_index as number) < 0) {
        return {
          error: 'section_index must be >= 0.',
          recovery: 'Use get_document to check the section count.',
        };
      }
      if (Array.isArray(args.section_indices)) {
        if (args.section_indices.length === 0) {
          return { error: 'section_indices is empty.', recovery: 'Provide at least one index.' };
        }
        for (const s of args.section_indices as number[]) {
          if (typeof s !== 'number' || s < 0) {
            return {
              error: `section_indices contains invalid value ${s}.`,
              recovery: 'All values must be non-negative integers.',
            };
          }
        }
      }
      return null;
    },
    getActivityDescription: (args) => {
      const loc = typeof args.location === 'string' ? args.location : 'footer';
      if (Array.isArray(args.section_indices))
        return `Inserting page numbers in ${loc} for ${args.section_indices.length} sections`;
      return `Inserting page numbers in ${loc}`;
    },
    execute: async (args) => {
      return await Word.run(async (context) => {
        const sections = context.document.sections;
        sections.load('items');
        await context.sync();

        const indices: number[] = Array.isArray(args.section_indices)
          ? (args.section_indices as number[])
          : [typeof args.section_index === 'number' ? (args.section_index as number) : 0];

        const loc = typeof args.location === 'string' ? args.location : 'footer';
        const format = typeof args.format === 'string' ? args.format : '{PAGE}';
        const alignment = typeof args.alignment === 'string' ? args.alignment : 'center';
        const prefix = typeof args.prefix_text === 'string' ? (args.prefix_text as string) : '';
        const suffix = typeof args.suffix_text === 'string' ? (args.suffix_text as string) : '';
        const fullTemplate = `${prefix}${format}${suffix}`;

        const perSection: Array<Record<string, unknown>> = [];

        // Collect each section's paragraph-collection proxy for alignment in a second sync.
        const paraCollections: Array<{ idx: number; paras: Word.ParagraphCollection }> = [];
        const parts = fullTemplate.split(/(\{PAGE\}|\{NUMPAGES\})/);

        for (const idx of indices) {
          const section = sections.items[idx];
          if (!section) {
            perSection.push({
              section_index: idx,
              error: `Section ${idx} out of range (document has ${sections.items.length} sections).`,
            });
            continue;
          }
          const target =
            loc === 'header'
              ? section.getHeader(Word.HeaderFooterType.primary)
              : section.getFooter(Word.HeaderFooterType.primary);
          target.clear();

          for (const part of parts) {
            if (part === '{PAGE}') {
              target.getRange('End').insertField('Before', 'Page');
            } else if (part === '{NUMPAGES}') {
              target.getRange('End').insertField('Before', 'NumPages');
            } else if (part) {
              target.insertText(part, 'End');
            }
          }

          const paras = target.paragraphs;
          paras.load('items');
          paraCollections.push({ idx, paras });
        }
        await context.sync(); // one sync: all clears + field inserts + paragraph loads committed

        const alignEnumMap: Record<string, Word.Alignment> = {
          left: Word.Alignment.left,
          center: Word.Alignment.centered,
          right: Word.Alignment.right,
        };
        for (const { idx, paras } of paraCollections) {
          if (paras.items.length > 0) {
            paras.items[0]!.alignment = alignEnumMap[alignment] ?? Word.Alignment.centered;
          }
          perSection.push({ section_index: idx, success: true });
        }
        await context.sync();

        const common = {
          location: loc,
          alignment,
          format: fullTemplate,
          warning: `Previous content in ${loc}${indices.length > 1 ? ' (each targeted section)' : ''} was cleared.`,
        };

        if (Array.isArray(args.section_indices)) {
          return JSON.stringify({
            success: perSection.every((r) => r.success),
            ...common,
            sections: perSection,
          });
        }
        const only = perSection[0]!;
        if (only.error) {
          return JSON.stringify({
            error: only.error,
            recovery: `Use an index from 0 to ${sections.items.length - 1}.`,
          });
        }
        return JSON.stringify({
          success: true,
          section_index: only.section_index,
          ...common,
        });
      });
    },
  }),

  buildWordTool({
    name: 'set_page_setup',
    description:
      "Set page layout for a document section: margins, orientation, and paper size. Use this to change page margins, switch between portrait/landscape, or set paper size. For content layout within the page (indents, spacing), use format_paragraph instead. PREREQUISITE: Call get_document to check section count. Only include properties you want to change — omitted properties keep their current values. WARNING: This tool requires the Word desktop application — it is NOT available in Word Online. Returns the properties applied.",
    parameters: {
      type: 'object',
      properties: {
        section_index: {
          type: 'number',
          description: 'Zero-based section index. Defaults to 0. Use get_document to check section count.',
        },
        orientation: {
          type: 'string',
          enum: ['portrait', 'landscape'],
          description: 'Page orientation.',
        },
        top_margin: { type: 'number', description: 'Top margin in points (72pt = 1 inch). Must be >= 0. Example: 72 = 1 inch.' },
        bottom_margin: { type: 'number', description: 'Bottom margin in points (72pt = 1 inch). Must be >= 0.' },
        left_margin: { type: 'number', description: 'Left margin in points (72pt = 1 inch). Must be >= 0.' },
        right_margin: { type: 'number', description: 'Right margin in points (72pt = 1 inch). Must be >= 0.' },
        paper_size: {
          type: 'string',
          enum: ['letter', 'a4', 'legal', 'a3', 'a5'],
          description: 'Paper size. Choose based on document purpose and regional convention.',
        },
      },
    },
    permission: true,
    validateInput: (args) => {
      const hasProperty = PAGE_SETUP_KEYS.some((k) => k in args);
      if (!hasProperty) {
        return {
          error: 'No page setup properties provided.',
          recovery: `Include at least one of: ${PAGE_SETUP_KEYS.join(', ')}.`,
        };
      }
      for (const key of MARGIN_KEYS) {
        if (typeof args[key] === 'number' && (args[key] as number) < 0) {
          return {
            error: `${key} must be >= 0.`,
            recovery: `Provide a non-negative value in points (72pt = 1 inch). Example: ${key}: 72.`,
          };
        }
      }
      return null;
    },
    getActivityDescription: (args) => {
      const changes: string[] = [];
      if (typeof args.orientation === 'string') changes.push(args.orientation);
      if (typeof args.paper_size === 'string') changes.push(args.paper_size);
      if (MARGIN_KEYS.some((k) => typeof args[k] === 'number')) changes.push('margins');
      return `Updating page setup${changes.length ? `: ${changes.join(', ')}` : ''}`;
    },
    execute: async (args) => {
      // PaperSize enum values are PascalCase — the public API does NOT accept lowercase.
      const PAPER_MAP: Record<string, Word.PaperSize> = {
        letter: Word.PaperSize.letter,
        a4: Word.PaperSize.a4,
        legal: Word.PaperSize.legal,
        a3: Word.PaperSize.a3,
        a5: Word.PaperSize.a5,
      };

      // Canonical feature detection lives on Office.context, NOT Word.RequestContext.
      // PageSetup requires WordApiDesktop 1.3 (Word Mac 16.88+, current Windows).
      const supportsPageSetup =
        typeof Office !== 'undefined' &&
        Office.context?.requirements?.isSetSupported?.('WordApiDesktop', '1.3') === true;

      if (!supportsPageSetup) {
        return JSON.stringify({
          error: 'Page setup is not available on this Word build.',
          recovery:
            'Requires WordApiDesktop 1.3 (Word for Mac 16.88+ or current Word desktop). Proceed with default page layout.',
        });
      }

      return await Word.run(async (context) => {
        const sections = context.document.sections;
        sections.load('items');
        await context.sync();

        const idx = typeof args.section_index === 'number' ? args.section_index : 0;
        const section = sections.items[idx];
        if (!section) {
          return JSON.stringify({
            error: `Section index ${idx} out of range (document has ${sections.items.length} sections).`,
            recovery: `Use 0 to ${sections.items.length - 1}. Call get_document to check section count.`,
          });
        }

        const applied: Record<string, unknown> = {};
        const pageSetup = section.pageSetup;

        try {
          if (args.orientation === 'landscape') {
            pageSetup.orientation = Word.PageOrientation.landscape;
            applied.orientation = 'landscape';
          } else if (args.orientation === 'portrait') {
            pageSetup.orientation = Word.PageOrientation.portrait;
            applied.orientation = 'portrait';
          }

          if (typeof args.top_margin === 'number') {
            pageSetup.topMargin = args.top_margin;
            applied.top_margin = args.top_margin;
          }
          if (typeof args.bottom_margin === 'number') {
            pageSetup.bottomMargin = args.bottom_margin;
            applied.bottom_margin = args.bottom_margin;
          }
          if (typeof args.left_margin === 'number') {
            pageSetup.leftMargin = args.left_margin;
            applied.left_margin = args.left_margin;
          }
          if (typeof args.right_margin === 'number') {
            pageSetup.rightMargin = args.right_margin;
            applied.right_margin = args.right_margin;
          }

          if (typeof args.paper_size === 'string') {
            const mapped = PAPER_MAP[args.paper_size as string];
            if (!mapped) {
              return JSON.stringify({
                error: `Unknown paper_size "${args.paper_size}".`,
                recovery: `Use one of: ${Object.keys(PAPER_MAP).join(', ')}.`,
              });
            }
            pageSetup.paperSize = mapped;
            applied.paper_size = args.paper_size;
          }
        } catch (setErr) {
          const msg = setErr instanceof Error ? setErr.message : String(setErr);
          return JSON.stringify({
            error: `Page setup property not supported on this host: ${msg}`,
            recovery:
              'This feature requires Word desktop. Proceed with default page layout.',
          });
        }

        await context.sync();

        return JSON.stringify({
          success: true,
          section_index: idx,
          applied,
          hint: 'Page layout changed. Content may have reflowed — use get_document to check.',
        });
      });
    },
  }),
];
